// Frontend-only video renderer.
// Takes 4 scenes (each with a preloaded HTMLImageElement + text) and produces
// a 1080x1920 vertical video entirely in the browser.
//
// Output format priority:
//   1) MP4 (H.264) via WebCodecs `VideoEncoder` + `mp4-muxer` — preferred,
//      since most social platforms (Instagram, TikTok, YouTube) accept MP4
//      directly and reject WebM.
//   2) WebM via Canvas captureStream + MediaRecorder — fallback for
//      browsers without WebCodecs support. Output is post-patched with a
//      real Duration tag using `fix-webm-duration` so playback controls work.
import fixWebmDuration from "fix-webm-duration";
import { Muxer, ArrayBufferTarget } from "mp4-muxer";

const CANVAS_W = 1080;
const CANVAS_H = 1920;
const DEFAULT_SCENE_SECONDS = 3.5;
const FPS = 30;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image failed to load"));
    img.src = src;
  });
}

// ----- text wrapping helpers -----
function wrapLines(ctx, text, maxWidth) {
  const words = (text || "").trim().split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? current + " " + w : w;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// ----- scene drawing -----
function drawSceneFrame(ctx, frame, progress) {
  // Defensive: if a degenerate frame slips through, paint a black background
  // and exit cleanly rather than crashing the rAF loop.
  const img = frame && frame.img;
  const text = (frame && frame.text) || "";
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  if (!img || !img.naturalWidth || !img.naturalHeight) return;

  // progress ∈ [0,1] within this scene. Drives the zoom.
  const zoom = 1.0 + progress * 0.08; // 0% → 8% zoom over scene

  // Cover-fit the image into the canvas, then apply zoom relative to centre.
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const canvasRatio = CANVAS_W / CANVAS_H;
  const imgRatio = iw / ih;
  let drawW, drawH;
  if (imgRatio > canvasRatio) {
    drawH = CANVAS_H;
    drawW = drawH * imgRatio;
  } else {
    drawW = CANVAS_W;
    drawH = drawW / imgRatio;
  }
  drawW *= zoom;
  drawH *= zoom;
  const dx = (CANVAS_W - drawW) / 2;
  const dy = (CANVAS_H - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);

  // Subtle bottom vignette to improve caption legibility.
  const grad = ctx.createLinearGradient(0, CANVAS_H * 0.45, 0, CANVAS_H);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, CANVAS_H * 0.45, CANVAS_W, CANVAS_H * 0.55);

  // Text overlay — centred horizontally, positioned around 65% height.
  const paddingX = 60;
  const maxTextWidth = CANVAS_W - paddingX * 2;
  const fontSize = 78;
  ctx.font = `900 ${fontSize}px Inter, system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const lines = wrapLines(ctx, text, maxTextWidth);
  const lineHeight = fontSize * 1.18;
  const totalTextH = lines.length * lineHeight;
  const centerY = CANVAS_H * 0.66;
  const startY = centerY - totalTextH / 2 + lineHeight / 2;

  // Dark semi-transparent background panel behind text.
  const bgPaddingX = 36;
  const bgPaddingY = 28;
  const bgTop = startY - lineHeight / 2 - bgPaddingY;
  const bgBottom = startY + (lines.length - 1) * lineHeight + lineHeight / 2 + bgPaddingY;
  const widestLine = lines.reduce(
    (max, l) => Math.max(max, ctx.measureText(l).width),
    0
  );
  const bgW = Math.min(CANVAS_W - 40, widestLine + bgPaddingX * 2);
  const bgX = (CANVAS_W - bgW) / 2;
  const bgH = bgBottom - bgTop;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  // Rounded rect.
  const r = 28;
  ctx.beginPath();
  ctx.moveTo(bgX + r, bgTop);
  ctx.lineTo(bgX + bgW - r, bgTop);
  ctx.quadraticCurveTo(bgX + bgW, bgTop, bgX + bgW, bgTop + r);
  ctx.lineTo(bgX + bgW, bgBottom - r);
  ctx.quadraticCurveTo(bgX + bgW, bgBottom, bgX + bgW - r, bgBottom);
  ctx.lineTo(bgX + r, bgBottom);
  ctx.quadraticCurveTo(bgX, bgBottom, bgX, bgBottom - r);
  ctx.lineTo(bgX, bgTop + r);
  ctx.quadraticCurveTo(bgX, bgTop, bgX + r, bgTop);
  ctx.closePath();
  ctx.fill();

  // Text fade-in during the first 300ms of the scene for smoothness.
  const textAlpha = Math.min(1, progress * 4);
  ctx.globalAlpha = textAlpha;

  lines.forEach((line, i) => {
    const y = startY + i * lineHeight;
    ctx.lineWidth = 10;
    ctx.strokeStyle = "rgba(0,0,0,0.9)";
    ctx.strokeText(line, CANVAS_W / 2, y);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(line, CANVAS_W / 2, y);
  });

  ctx.globalAlpha = 1;
}

// ----- MediaRecorder mime selection -----
function pickMime() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return "video/webm";
}

// ----- main export -----
export async function renderVideoFromScenes(opts) {
  const { scenes, sceneSeconds = DEFAULT_SCENE_SECONDS, onProgress, isAborted } = opts;
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error("No scenes provided");
  }

  // Coerce + preload up-front so both MP4 and WebM paths share the same prep.
  const aborted = () => (typeof isAborted === "function" ? !!isAborted() : false);
  const safeScenes = scenes.map((s) => ({
    text: ((s && s.text) || "").toString(),
    image_url: s && s.image_url,
  }));

  onProgress?.({ phase: "preloading", progress: 0 });
  const images = [];
  for (let i = 0; i < safeScenes.length; i++) {
    const img = await loadImage(safeScenes[i].image_url);
    images.push(img);
    onProgress?.({ phase: "preloading", progress: (i + 1) / safeScenes.length });
  }
  if (aborted()) throw new Error("Aborted");

  const frames = safeScenes.map((s, i) => ({ img: images[i], text: s.text }));

  // Try WebCodecs/MP4 first — drops back to WebM on any failure.
  if (await canEncodeMp4()) {
    try {
      return await renderViaWebCodecs({ frames, sceneSeconds, onProgress, aborted });
    } catch (e) {
      // If WebCodecs encoding fails for any reason (driver issue, codec
      // mismatch, OOM during a real render) fall through to MediaRecorder
      // so the user still gets a usable video.
      // eslint-disable-next-line no-console
      console.warn("[videoRenderer] WebCodecs path failed, falling back to WebM:", e);
    }
  }
  return renderViaMediaRecorder({ frames, sceneSeconds, onProgress, aborted });
}

// ─────────────────────────────────────────────────────────────────────
// MP4 PATH — WebCodecs VideoEncoder + mp4-muxer
// ─────────────────────────────────────────────────────────────────────
const MP4_CODEC = "avc1.42001f"; // H.264 Baseline @ Level 3.1 — universal compatibility

async function canEncodeMp4() {
  if (typeof window === "undefined") return false;
  if (typeof window.VideoEncoder === "undefined") return false;
  if (typeof window.VideoFrame === "undefined") return false;
  try {
    const support = await window.VideoEncoder.isConfigSupported({
      codec: MP4_CODEC,
      width: CANVAS_W,
      height: CANVAS_H,
      bitrate: 5_000_000,
      framerate: FPS,
    });
    return !!(support && support.supported);
  } catch {
    return false;
  }
}

async function renderViaWebCodecs({ frames, sceneSeconds, onProgress, aborted }) {
  const sceneMs = sceneSeconds * 1000;
  const sceneCount = frames.length;
  const totalFrames = Math.round(FPS * sceneSeconds * sceneCount);

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d", { alpha: false });

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: "avc", width: CANVAS_W, height: CANVAS_H, frameRate: FPS },
    fastStart: "in-memory", // moves the moov atom to the front so the file
                            // streams cleanly without a final rewrite pass
  });

  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => {
      // eslint-disable-next-line no-console
      console.error("[videoRenderer] VideoEncoder error:", e);
    },
  });
  encoder.configure({
    codec: MP4_CODEC,
    width: CANVAS_W,
    height: CANVAS_H,
    bitrate: 5_000_000, // 5 Mbps — crisper than the WebM path; H.264 compresses well
    framerate: FPS,
    avc: { format: "avc" },
  });

  // Frame loop — fully synchronous, deterministic timestamps.
  // Encode every frame, force a keyframe every 1 second so seeking is fast.
  const frameDurationUs = Math.round(1_000_000 / FPS);
  for (let i = 0; i < totalFrames; i++) {
    if (aborted()) {
      try { encoder.close(); } catch { /* */ }
      throw new Error("Aborted");
    }

    const elapsedMs = (i / FPS) * 1000;
    const sceneIndex = Math.max(0, Math.min(sceneCount - 1, Math.floor(elapsedMs / sceneMs)));
    const withinScene = (elapsedMs % sceneMs) / sceneMs;
    drawSceneFrame(ctx, frames[sceneIndex], withinScene);

    const timestamp = i * frameDurationUs;
    const frame = new window.VideoFrame(canvas, { timestamp, duration: frameDurationUs });
    const keyFrame = i % FPS === 0; // 1 keyframe / second
    encoder.encode(frame, { keyFrame });
    frame.close();

    if (i % 10 === 0) {
      onProgress?.({ phase: "rendering", progress: i / totalFrames });
      // Yield once every 10 frames so the UI can repaint progress bars.
      // Microtask queue drain — keeps encode tight without blocking forever.
      // eslint-disable-next-line no-await-in-loop
      await Promise.resolve();
    }
  }

  // Render a final "ended" frame to guarantee a clean last image.
  drawSceneFrame(ctx, frames[frames.length - 1], 1);

  await encoder.flush();
  encoder.close();
  muxer.finalize();

  const { buffer } = muxer.target;
  const blob = new Blob([buffer], { type: "video/mp4" });
  const url = URL.createObjectURL(blob);
  onProgress?.({ phase: "rendering", progress: 1 });
  return { blob, url, mime: "video/mp4" };
}

// ─────────────────────────────────────────────────────────────────────
// WEBM PATH — Canvas captureStream + MediaRecorder (fallback)
// ─────────────────────────────────────────────────────────────────────
async function renderViaMediaRecorder({ frames, sceneSeconds, onProgress, aborted }) {
  const lastFrame = frames[frames.length - 1];

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d", { alpha: false });

  const stream = canvas.captureStream(FPS);
  const mime = pickMime();
  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: 3_000_000,
  });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const stopped = new Promise((resolve) => { recorder.onstop = resolve; });

  drawSceneFrame(ctx, frames[0], 0);
  recorder.start();

  const sceneMs = sceneSeconds * 1000;
  const totalDurationMs = frames.length * sceneMs;
  const startTs = performance.now();

  await new Promise((resolve) => {
    const tick = (now) => {
      if (aborted()) { resolve(); return; }
      const elapsed = now - startTs;
      if (elapsed >= totalDurationMs) {
        drawSceneFrame(ctx, lastFrame, 1);
        resolve();
        return;
      }
      const rawIdx = Math.floor(elapsed / sceneMs);
      const sceneIndex = Math.max(0, Math.min(frames.length - 1, rawIdx));
      drawSceneFrame(ctx, frames[sceneIndex] || lastFrame, (elapsed % sceneMs) / sceneMs);
      onProgress?.({ phase: "rendering", progress: elapsed / totalDurationMs });
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

  if (recorder.state !== "inactive") recorder.stop();
  await stopped;
  stream.getTracks().forEach((t) => t.stop());

  // Patch the WebM duration tag so the seek bar / replay / time display work.
  const knownDurationMs = frames.length * sceneMs;
  const rawBlob = new Blob(chunks, { type: mime });
  let blob = rawBlob;
  try {
    blob = await fixWebmDuration(rawBlob, knownDurationMs, { logger: false });
  } catch {
    blob = rawBlob;
  }
  const url = URL.createObjectURL(blob);
  return { blob, url, mime };
}

// Trigger an automatic download of a blob URL.
export function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
